import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, MenuItem, TextField, Typography } from '@mui/material';
import useExpenseLookups from '../../components/expense/useExpenseLookups';
import { formatDate } from '../../services/expenseVouchersApi';
import { AGEING_OPTIONS, formatMoney, reportsApi } from '../../services/expenseAccountsApi';
import { PaymentStatusChip, useAccountsBase } from '../../components/expense/voucherShared';
import { DateRangeFilter, EmployeeFilter } from '../../components/expense/AccountsFilters';
import {
  CurrencyTotals, ExportButton, FilterBar, ReportsHeader, ReportTable, useReport, WalletPositions,
} from '../../components/expense/ReportShared';

const EMPTY_FILTERS = {
  vendor: '', employeeId: '', currency: '', employmentCountryId: '', jobNameId: '', paymentStatus: '', ageing: '', approvedFrom: '', approvedTo: '',
};

// Only booked vouchers that still owe money can be payables
const PAYABLE_STATUS_OPTIONS = [
  { value: '', label: 'All outstanding' },
  { value: 'IN_PROCESS', label: 'In Process' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
  { value: 'ON_HOLD', label: 'On Hold' },
];

const AGEING_COLORS = { '0_30': 'default', '31_60': 'info', '61_90': 'warning', '90_PLUS': 'error' };
const ageingLabel = (key) => AGEING_OPTIONS.find((o) => o.value === key)?.label || '-';

// Accounts Payables / Outstanding: booked vouchers with a balance still to pay.
// Ageing = days since approval. Totals and ageing are per currency.
const AccountsPayablesReport = () => {
  const navigate = useNavigate();
  const accountsBase = useAccountsBase();
  const { countries, currencies, jobNames, users } = useExpenseLookups(['countries', 'currencies', 'jobNames', 'users']);
  const report = useReport(reportsApi.payables, { emptyFilters: EMPTY_FILTERS, defaultSort: { sort: 'ageing', order: 'desc' } });
  const { filters, setFilter, setFilterValue } = report;
  const money = (row, field) => formatMoney(row[field], row.currency);
  const ageing = report.response?.ageing || [];

  const columns = [
    { label: 'Voucher No.', sortKey: 'voucherNumber', nowrap: true, render: (r) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.voucherNumber}</Typography> },
    { label: 'Voucher Date', sortKey: 'voucherDate', nowrap: true, render: (r) => formatDate(r.voucherDate) },
    { label: 'Approved', sortKey: 'approvedAt', nowrap: true, render: (r) => formatDate(r.approvedAt) },
    {
      label: 'Ageing', sortKey: 'ageing', nowrap: true,
      render: (r) => <Chip size="small" color={AGEING_COLORS[r.ageingBucket]} label={`${r.ageingDays} day${r.ageingDays === 1 ? '' : 's'}`} title={ageingLabel(r.ageingBucket)} />,
    },
    { label: 'Employee', sortKey: 'employee', render: (r) => r.employeeName },
    { label: 'Vendor', render: (r) => r.vendors.join(', ') || '-' },
    { label: 'Project', render: (r) => r.jobName || '-' },
    { label: 'Currency', nowrap: true, render: (r) => r.currency },
    { label: 'Cost to Project', nowrap: true, align: 'right', render: (r) => money(r, 'costToProject') },
    {
      label: 'Wallet Adj.',
      nowrap: true,
      align: 'right',
      render: (r) => (r.walletPosted ? money(r, 'walletAdjustment') : <Typography variant="caption" color="text.secondary">at 1st payment</Typography>),
    },
    {
      label: 'Net Payable',
      sortKey: 'netPayable',
      nowrap: true,
      align: 'right',
      render: (r) => <>{money(r, 'netPayable')}{!r.walletPosted && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>estimated</Typography>}</>,
    },
    { label: 'Paid', sortKey: 'paid', nowrap: true, align: 'right', render: (r) => money(r, 'paidTotal') },
    { label: 'Balance Payable', sortKey: 'balance', nowrap: true, align: 'right', render: (r) => <strong>{money(r, 'balancePayable')}</strong> },
    { label: 'Payment Status', sortKey: 'paymentStatus', render: (r) => <PaymentStatusChip status={r.paymentStatus} /> },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <ReportsHeader
        title="Payables / Outstanding"
        subtitle="Booked vouchers with a balance still to pay. Net Payable is net of the employee's wallet (estimated until the first payment posts it). Fully paid, cancelled, rejected and unbooked vouchers are not payables."
        actions={<ExportButton onExport={reportsApi.exportPayables} params={report.params} />}
      />

      <FilterBar onClear={report.clear}>
        <TextField size="small" placeholder="Search voucher, employee, vendor, project..." value={report.search} onChange={(e) => report.setSearch(e.target.value)} sx={{ minWidth: 280 }} />
        <EmployeeFilter users={users} value={filters.employeeId} onChange={setFilterValue('employeeId')} />
        <TextField size="small" label="Vendor" placeholder="Vendor name" value={filters.vendor} onChange={setFilter('vendor')} sx={{ minWidth: 170 }} />
        <TextField select size="small" label="Currency" value={filters.currency} onChange={setFilter('currency')} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          {currencies.map((c) => <MenuItem key={c.value} value={c.value}>{c.value}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Country" value={filters.employmentCountryId} onChange={setFilter('employmentCountryId')} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          {countries.map((c) => <MenuItem key={c.value} value={c.value}>{c.raw?.name || c.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Project" value={filters.jobNameId} onChange={setFilter('jobNameId')} sx={{ minWidth: 170 }}>
          <MenuItem value="">All</MenuItem>
          {jobNames.map((j) => <MenuItem key={j.value} value={j.value}>{j.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Payment status" value={filters.paymentStatus} onChange={setFilter('paymentStatus')} sx={{ minWidth: 170 }}>
          {PAYABLE_STATUS_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Ageing" value={filters.ageing} onChange={setFilter('ageing')} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {AGEING_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <DateRangeFilter label="Approved" from={filters.approvedFrom} to={filters.approvedTo}
          onFromChange={setFilterValue('approvedFrom')} onToChange={setFilterValue('approvedTo')} />
      </FilterBar>

      <CurrencyTotals
        testId="payables-totals"
        summary={report.summary}
        title={(s) => `${s.currency} - ${s.voucherCount} voucher${s.voucherCount === 1 ? '' : 's'}`}
        lines={(s) => {
          const buckets = ageing.find((a) => a.currency === s.currency)?.buckets || [];
          return [
            `Balance Payable ${formatMoney(s.balancePayable, s.currency)}`,
            `Net Payable ${formatMoney(s.netPayable, s.currency)} - Paid ${formatMoney(s.paid, s.currency)}`,
            buckets.filter((b) => b.count).map((b) => `${b.label}: ${formatMoney(b.balance, s.currency)}`).join(' | '),
          ];
        }}
      />

      <WalletPositions positions={report.response?.walletPositions} />

      <ReportTable report={report} columns={columns} emptyText="No outstanding payables." onRowClick={(r) => navigate(`${accountsBase}/${r._id}`)} />
    </Box>
  );
};

export default AccountsPayablesReport;
